import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { SxProps, Typography, TypographyProps, useMediaQuery, useTheme } from '@mui/material';
import { Asset, Maybe, Page, NavigationLink as TNavigationLink, AnalyticsContext, BlogDetailsPage, FacilityDetailsPage, ServiceDetailsPage } from '@/types/generated';
import { getNavItemStyles, getNavItemTypographyVariant, imageStyles, getNavLinkStyles } from './NavigationLink.styles';
import ImageWrapper from '@shared-components/components/ImageWrapper';
import TAGLink from '@shared-components/components/TAGLink';
import { useAppContext, useFacilityContext, useOfficeLocationContext } from '@shared-components/context';
import { resolveEntryLink } from '@utils/resolveButtonEntryLink';
export interface INavigationLinkProps extends TNavigationLink {
  isInternal: boolean;
  itemIndex: number;
  linkText?: Maybe<string>;
  linkColor?: string;
  pageData?: Maybe<Page | BlogDetailsPage | FacilityDetailsPage | ServiceDetailsPage>;
  externalUrl?: Maybe<string>;
  openInNewTab?: Maybe<boolean>;
  linkIcon?: Maybe<Asset>;
  dataTestId?: string;
  isUtilityNavigation?: boolean;
  disableUnderline?: boolean;
  compactMobileMenuStyle?: boolean;
  eventType?: string;
  navigationType?: string;
  isTextColorLight?: boolean;
  analyticsContext?: Maybe<AnalyticsContext>;
  navLinkSx?: SxProps;
  showIconText?: boolean;
  iconSize?: number;
  trailingIcon?: boolean;
  textVariant?: TypographyProps['variant'];
}
interface INavigationLink {
  isInternal: boolean;
  pageData?: Maybe<Page | BlogDetailsPage | FacilityDetailsPage | ServiceDetailsPage>;
  linkText?: Maybe<string>;
  externalUrl?: Maybe<string>;
  analyticsContext?: Maybe<AnalyticsContext>;
}
export function resolveNavLinkText({
  linkText,
  isInternal,
  pageData,
  externalUrl
}: INavigationLink) {
  if (linkText) {
    return linkText;
  }
  if (isInternal && pageData?.__typename === 'Page') {
    return pageData?.breadcrumbTitle ?? pageData?.title ?? '';
  }
  return externalUrl ?? '';
}
export default function NavigationLink({
  linkText,
  pageData,
  linkIcon,
  itemIndex,
  isInternal,
  externalUrl,
  openInNewTab,
  analyticsContext,
  dataTestId = 'nav_link',
  linkColor = '',
  disableUnderline = false,
  isTextColorLight = false,
  isUtilityNavigation = false,
  compactMobileMenuStyle = false,
  navLinkSx,
  showIconText = false,
  iconSize,
  trailingIcon = false,
  textVariant
}: Readonly<INavigationLinkProps>) {
  const {
    config
  } = useAppContext();
  const {
    officeInfo
  } = useFacilityContext();
  const {
    officeInfo: officeLocationInfo
  } = useOfficeLocationContext();
  const href = isInternal ? resolveEntryLink({
    path: pageData?.path ?? '',
    typeName: pageData?.__typename as string,
    config,
    officeInfo,
    officeLocationInfo
  }) : externalUrl ?? '#';
  const text = resolveNavLinkText({
    linkText,
    isInternal,
    pageData,
    externalUrl
  });
  const withIcon = !!linkIcon?.url || false;
  const title = (isInternal ? pageData?.title : externalUrl) ?? undefined;
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));
  const showDisplayText = withIcon && showIconText || !withIcon;
  return <TAGLink isInternal={isInternal} title={title} href={href || '#'} dataTestId={dataTestId} analyticsContext={analyticsContext} openInNewTab={openInNewTab ?? false} linkSx={{
    ...getNavLinkStyles(isMobileScreen, itemIndex, isUtilityNavigation, compactMobileMenuStyle),
    ...navLinkSx
  }} data-sentry-element="TAGLink" data-sentry-component="NavigationLink" data-sentry-source-file="NavigationLink.tsx">
			{withIcon && <ImageWrapper src={linkIcon?.url as string} alt={linkIcon?.description ?? ''} style={imageStyles(iconSize) as React.CSSProperties} />}
			{showDisplayText && <Typography variant={textVariant ?? getNavItemTypographyVariant(isMobileScreen, isUtilityNavigation)} color={isTextColorLight ? 'text.light' : linkColor} sx={{
      ...getNavItemStyles({
        isMobileScreen,
        isUtilityNavigation,
        withIcon,
        disableUnderline,
        compactMobileMenuStyle,
        isTextColorLight
      }),
      ...(trailingIcon && {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem'
      })
    }}>
					{text}
					{trailingIcon && <ArrowForwardIcon fontSize="small" sx={{
        color: 'tertiary.main'
      }} data-testid={`${dataTestId}_arrow`} />}
				</Typography>}
		</TAGLink>;
}